// src/components/staff/mobile/MobileStaffRequests.jsx
// Utilise la vraie route /staff/client-requests (source: ClientRequestsView.jsx)
// Visible to: Supervisor (75+), Manager (90+), Admin (100)

import React, { useState, useEffect, useCallback } from 'react';
import { Fuel, Wrench, RefreshCw, ChevronDown, ChevronUp, X, Check, XCircle, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { getAllClientRequests, approveClientRequest, rejectClientRequest, completeClientRequest } from '../../../services/staffApi';

const GLOBAL_STYLE = `@keyframes spin { to { transform: rotate(360deg) } }`;

const STATUS = {
  pending:   { label: 'Pending',   bg: 'rgba(234,179,8,0.15)',   color: '#eab308', Icon: Clock },
  approved:  { label: 'Approved',  bg: 'rgba(34,197,94,0.15)',   color: '#22c55e', Icon: CheckCircle },
  completed: { label: 'Completed', bg: 'rgba(59,130,246,0.15)',  color: '#3b82f6', Icon: CheckCircle },
  rejected:  { label: 'Rejected',  bg: 'rgba(239,68,68,0.15)',   color: '#ef4444', Icon: XCircle },
  cancelled: { label: 'Cancelled', bg: 'rgba(100,116,139,0.15)', color: '#64748b', Icon: XCircle },
};

const PRIORITY = {
  normal: { label: 'Normal', color: '#64748b' },
  high:   { label: 'High',   color: '#fb923c' },
  urgent: { label: 'Urgent', color: '#ef4444' },
};

const Badge = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: s.bg, color: s.color }}>
      <s.Icon size={9} /> {s.label}
    </span>
  );
};

const inputStyle = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 12, padding: '12px 14px', color: '#f1f5f9', fontSize: 15,
  outline: 'none', boxSizing: 'border-box',
  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
};

// ─── Reject Bottom Sheet ──────────────────────────────────────────────────────
const RejectSheet = ({ open, onClose, onConfirm, saving }) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#1e293b', borderRadius: '24px 24px 0 0', paddingBottom: 'env(safe-area-inset-bottom)', animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1)' }}>
        <style>{`@keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#334155' }} />
        </div>
        <div style={{ padding: '4px 20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9' }}>Reject Request</span>
            <button onClick={onClose} style={{ background: '#334155', border: 'none', borderRadius: 20, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <X size={15} color="#94a3b8" />
            </button>
          </div>
          <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>Reason for rejection *</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="Explain why this request is being rejected…" style={{ ...inputStyle, resize: 'none', lineHeight: 1.5, marginBottom: 14 }} />
          <button onClick={() => reason.trim() && onConfirm(reason)} disabled={saving || !reason.trim()} style={{ width: '100%', padding: '14px', borderRadius: 14, background: reason.trim() ? '#ef4444' : '#1e293b', border: 'none', color: '#fff', fontSize: 15, fontWeight: 600, cursor: reason.trim() ? 'pointer' : 'default', opacity: reason.trim() ? 1 : 0.5 }}>
            {saving ? 'Processing…' : 'Confirm Rejection'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Request Card ─────────────────────────────────────────────────────────────
const RequestCard = ({ req, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [rejectSheet, setRejectSheet] = useState(false);
  const [saving, setSaving] = useState(false);

  const isFuel = req.request_type === 'fuel';
  const accent = isFuel ? '#fb923c' : '#f87171';
  const Icon = isFuel ? Fuel : Wrench;
  const priority = req.priority ?? 'normal';
  const pColor = PRIORITY[priority]?.color ?? '#64748b';

  const formatDate = (s) => {
    if (!s) return 'N/A';
    const d = new Date(s);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleApprove = async () => {
    setSaving(true);
    try { await approveClientRequest(req.id); onRefresh(); }
    catch { alert('Failed to approve request'); }
    finally { setSaving(false); }
  };

  const handleReject = async (reason) => {
    setSaving(true);
    try { await rejectClientRequest(req.id, reason); setRejectSheet(false); onRefresh(); }
    catch { alert('Failed to reject request'); }
    finally { setSaving(false); }
  };

  const handleComplete = async () => {
    setSaving(true);
    try { await completeClientRequest(req.id); onRefresh(); }
    catch { alert('Failed to complete request'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div style={{ background: '#1e293b', borderRadius: 18, border: `0.5px solid ${priority === 'urgent' ? 'rgba(239,68,68,0.35)' : '#334155'}`, overflow: 'hidden' }}>
        <button onClick={() => setExpanded(e => !e)} style={{ width: '100%', background: 'none', border: 'none', padding: '14px 16px', textAlign: 'left', cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: `${accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={18} color={accent} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
                  {isFuel ? `${req.quantity || '?'} gallons` : (req.tail_number || 'Maintenance')}
                </span>
                <Badge status={req.status} />
                {priority !== 'normal' && (
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: `${pColor}18`, color: pColor }}>{priority.toUpperCase()}</span>
                )}
              </div>
              <p style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {req.client_first_name} {req.client_last_name}
                {req.company_name ? ` (${req.company_name})` : ''}
              </p>
              <p style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{formatDate(req.created_at)}</p>
            </div>
            {expanded ? <ChevronUp size={15} color="#475569" /> : <ChevronDown size={15} color="#475569" />}
          </div>
        </button>

        {expanded && (
          <div style={{ borderTop: '0.5px solid #334155', padding: '12px 16px' }}>
            {/* Details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {req.description && (
                <div>
                  <span style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description</span>
                  <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{req.description}</p>
                </div>
              )}
              {req.scheduled_date && (
                <div>
                  <span style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Scheduled</span>
                  <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{formatDate(req.scheduled_date)}</p>
                </div>
              )}
              {req.tail_number && isFuel && (
                <div>
                  <span style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aircraft</span>
                  <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{req.tail_number}</p>
                </div>
              )}
              {req.approved_by_first_name && (
                <div>
                  <span style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Approved by</span>
                  <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{req.approved_by_first_name} {req.approved_by_last_name}</p>
                </div>
              )}
              {req.notes && (
                <div>
                  <span style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</span>
                  <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{req.notes}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            {req.status === 'pending' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleApprove} disabled={saving} style={{ flex: 1, padding: '11px', borderRadius: 12, background: 'rgba(34,197,94,0.15)', border: '0.5px solid rgba(34,197,94,0.3)', color: '#22c55e', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  <Check size={14} /> Approve
                </button>
                <button onClick={() => setRejectSheet(true)} disabled={saving} style={{ flex: 1, padding: '11px', borderRadius: 12, background: 'rgba(239,68,68,0.12)', border: '0.5px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  <XCircle size={14} /> Reject
                </button>
              </div>
            )}
            {req.status === 'approved' && (
              <button onClick={handleComplete} disabled={saving} style={{ width: '100%', padding: '11px', borderRadius: 12, background: 'rgba(59,130,246,0.15)', border: '0.5px solid rgba(59,130,246,0.3)', color: '#3b82f6', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                <CheckCircle size={14} /> Mark as Completed
              </button>
            )}
          </div>
        )}
      </div>

      <RejectSheet open={rejectSheet} onClose={() => setRejectSheet(false)} onConfirm={handleReject} saving={saving} />
    </>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const MobileStaffRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({ pending: 0, approved: 0, completed: 0, rejected: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== 'all') params.request_type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      const r = await getAllClientRequests(params);
      const all = r.data.requests || [];
      setRequests(all);
      setStats({
        pending:   all.filter(x => x.status === 'pending').length,
        approved:  all.filter(x => x.status === 'approved').length,
        completed: all.filter(x => x.status === 'completed').length,
        rejected:  all.filter(x => x.status === 'rejected').length,
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: '0 16px 100px' }}>
      <style>{GLOBAL_STYLE}</style>

      {/* Stats mini row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 14 }}>
        {[
          { label: 'Pending',   value: stats.pending,   color: '#eab308' },
          { label: 'Approved',  value: stats.approved,  color: '#22c55e' },
          { label: 'Done',      value: stats.completed, color: '#3b82f6' },
          { label: 'Rejected',  value: stats.rejected,  color: '#ef4444' },
        ].map(s => (
          <div key={s.label} style={{ background: '#1e293b', borderRadius: 12, padding: '10px 6px', textAlign: 'center', border: '0.5px solid #334155' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 9, color: '#64748b', marginTop: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Type filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {[{ id: 'all', label: 'All' }, { id: 'fuel', label: '⛽ Fuel' }, { id: 'maintenance', label: '🔧 Maintenance' }].map(t => (
          <button key={t.id} onClick={() => setFilterType(t.id)} style={{
            flex: 1, padding: '9px 4px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 12,
            fontWeight: filterType === t.id ? 700 : 400,
            background: filterType === t.id ? 'rgba(59,130,246,0.18)' : '#1e293b',
            color: filterType === t.id ? '#3b82f6' : '#64748b',
            outline: filterType === t.id ? '1px solid rgba(59,130,246,0.35)' : '0.5px solid #334155',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 14 }}>
        {['all', 'pending', 'approved', 'completed', 'rejected'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            flexShrink: 0, padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 11,
            fontWeight: filterStatus === s ? 600 : 400,
            background: filterStatus === s ? '#3b82f6' : '#1e293b',
            color: filterStatus === s ? '#fff' : '#64748b',
          }}>
            {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Refresh */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={load} style={{ background: '#1e293b', border: '0.5px solid #334155', borderRadius: 10, padding: '8px 10px', cursor: 'pointer' }}>
          <RefreshCw size={15} color="#94a3b8" />
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
          <p style={{ color: '#475569' }}>No requests found</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {requests.map(req => <RequestCard key={req.id} req={req} onRefresh={load} />)}
        </div>
      )}
    </div>
  );
};

export default MobileStaffRequests;